import { useEffect, useReducer, Dispatch } from 'react';
import deepEqual from 'fast-deep-equal/es6';
import { usePrevious } from './usePrevious';

type PersistedReducer<State, Action> = [State, Dispatch<Action>];

export function usePersistedReducer<State, Action>(
  reducer: (state: State, action: Action) => State,
  initialState: State,
  storageKey: string,
  options: {
    mergeStrategy?: 'prefer-prev' | 'prefer-new' | 'shallow-merge';
    clearOnUnload?: boolean;
  } = { mergeStrategy: 'prefer-prev', clearOnUnload: true }
): PersistedReducer<State, Action> {
  // Initialize state with priority given to persisted state
  const [state, dispatch] = useReducer(reducer, initialState, () => {
    const stringState = localStorage.getItem(storageKey);
    if (stringState) {
      try {
        const parsedState = JSON.parse(stringState);
        
        // Apply merge strategy
        switch (options.mergeStrategy) {
          case 'prefer-prev':
            return { ...initialState, ...parsedState };
          case 'prefer-new':
            return { ...parsedState, ...initialState };
          case 'shallow-merge':
            return { ...initialState, ...parsedState };
          default:
            return parsedState;
        }
      } catch (error) {
        console.error('Failed to parse persisted state:', error);
        return initialState;
      }
    }
    return initialState;
  });

  const prevState = usePrevious(state);

  // Clean up on unload if configured
  useEffect(() => {
    if (!options.clearOnUnload) return;

    const handleBeforeUnload = () => {
      localStorage.removeItem(storageKey);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [storageKey, options.clearOnUnload]);

  // Persist state changes
  useEffect(() => {
    if (!prevState) return; // Skip initial render

    const stateEqual = deepEqual(prevState, state);
    if (!stateEqual) {
      try {
        const stringifiedState = JSON.stringify(state);
        localStorage.setItem(storageKey, stringifiedState);
      } catch (error) {
        console.error('Failed to persist state:', error);
      }
    }
  }, [prevState, state, storageKey]);

  return [state, dispatch];
}